import * as React from 'react';
import { Text } from '../Text';
import { st, classes } from './FileUpload.st.css';
import { FileUploadProps, FileUploadDefaultProps } from './types';
import { Fieldset } from '../internal/Fieldset';
import { DATA_HOOKS } from './dataHooks';
import { Button } from '../Button';
import FileUploadPopover from './components/FileUploadPopover';
import { Add } from '@wix/wix-ui-icons-common';
import File from './components/File';
import { getAriaDescribedBy } from '../../common/a11y';
import { TextButton } from '../TextButton';
import uniqueId from '../../common/uniqueId';

export const FileUpload: React.FC<FileUploadProps> = ({
  accept,
  label,
  errorMessage,
  files,
  multiple = false,
  buttonText,
  explanationText,
  onChange,
  onDeleteClick,
  deleteText,
  className,
  disabled,
  lang,
  ...rest
}) => {
  const fileInputRef = React.useRef(null);
  const clickHandler = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const [checkErrorFocus, setCheckErrorFocus] = React.useState(false);
  const [textBelowButtonRef, setTextBelowButtonRef] = React.useState<
    HTMLElement | TextButton
  >(null);

  const fileHasError = Boolean(files?.find((file) => file.state === 'error'));
  const hasError = Boolean(errorMessage || fileHasError);
  const filesExist = files && Boolean(files.length);
  const isOneFile = filesExist && files.length === 1;
  const handleDeleteClick = (i) => {
    const fileToDelete = files[i].file;
    if (
      fileInputRef.current &&
      `${fileInputRef.current.value}`.includes(fileToDelete.name)
    ) {
      fileInputRef.current.value = '';
    }
    onDeleteClick?.(i);
  };
  const generateFiles = () => {
    if (isOneFile) {
      return (
        <div
          className={classes.oneFile}
          ref={setTextBelowButtonRef}
          tabIndex={0}
          aria-describedby={uniqueErrorId}
        >
          <File
            file={files[0]}
            onDeleteClick={() => handleDeleteClick(0)}
            deleteText={deleteText}
            dataHook={DATA_HOOKS.singleFile}
          />
        </div>
      );
    }
    return (
      <FileUploadPopover
        files={files}
        onDeleteClick={handleDeleteClick}
        deleteText={deleteText}
        explanationText={explanationText}
        hasError={hasError}
        setTextBelowButtonRef={setTextBelowButtonRef}
      />
    );
  };

  const generateExplanation = () => {
    if (!explanationText) {
      return undefined;
    }
    return (
      <Text data-hook={DATA_HOOKS.explanation} className={classes.explanation}>
        {explanationText}
      </Text>
    );
  };

  const getErrorMessage = () => {
    if (hasError && isOneFile) {
      return files[0].errorMessage || errorMessage;
    }

    return errorMessage;
  };

  const uniqueErrorId = uniqueId('error-message');

  const ariaDescribedBy = getAriaDescribedBy([
    rest['aria-describedby'],
    hasError ? uniqueErrorId : '',
  ]);

  const handleFileChange = (_files: FileList) => {
    setTextBelowButtonRef(null);
    onChange(_files);
    setCheckErrorFocus(true);
  };

  React.useEffect(() => {
    if (checkErrorFocus && filesExist && fileHasError && textBelowButtonRef) {
      textBelowButtonRef.focus();
      setCheckErrorFocus(false);
    }
  }, [checkErrorFocus, filesExist, fileHasError, textBelowButtonRef]);

  return (
    <Fieldset
      data-error={hasError}
      dataHook={rest['data-hook']}
      className={st(classes.root, className)}
      label={label}
      errorMessage={getErrorMessage()}
      errorMessageId={uniqueErrorId}
      lang={lang}
    >
      <div className={classes.mainContainer}>
        <input
          className={classes.input}
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={(e) => handleFileChange(e.currentTarget.files)}
          multiple={multiple}
        />
        <Button
          className={classes.button}
          onClick={clickHandler}
          disabled={disabled}
          aria-describedby={ariaDescribedBy}
          aria-label={rest['aria-label']}
          upgrade
          ref={buttonRef}
          prefixIcon={<Add />}
        >
          {buttonText}
        </Button>
        {filesExist ? generateFiles() : generateExplanation()}
      </div>
    </Fieldset>
  );
};

FileUpload.displayName = 'FileUpload';
FileUpload.defaultProps = {
  multiple: false,
} as FileUploadDefaultProps;
